import { useQuery } from "@apollo/client";
import useCapabilities from "./useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { ALL_SECTIONS_QUERY } from "@/pages/sections/queries";
import { Section } from "@/types";

/**
 * Hook to check if the current user has a primary section assignment.
 * Returns the GraphQL section ID if the user is a section staff/admin with an assigned section.
 *
 * The effective GraphQL user contains legacy section IDs (e.g. PGA IDs), so
 * this hook fetches all sections and maps the legacy ID to the GraphQL node ID.
 */
export function usePrimarySection(): {
  hasPrimarySection: boolean;
  primarySectionId: string | null;
  loading: boolean;
} {
  const { hasRole, loaded: capabilitiesLoaded, sectionLegacyIds, sectionLegacyIdsLoading } = useCapabilities();

  const isSectionUser = capabilitiesLoaded && (hasRole(ROLE.SECTION_STAFF) || hasRole(ROLE.SECTION_ADMIN));

  const { data: sectionsData, loading: sectionsLoading } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY, {
    skip: !isSectionUser
  });

  let primarySectionId: string | null = null;
  if (!sectionLegacyIdsLoading && isSectionUser && sectionLegacyIds.length && sectionsData?.sections) {
    const primarySectionLegacyId = sectionLegacyIds[0];
    const matchedSection = sectionsData.sections.find(
      (s) => s.legacyId === primarySectionLegacyId || s.id === primarySectionLegacyId
    );
    primarySectionId = matchedSection?.id ?? null;
  }

  const loading = !capabilitiesLoaded || sectionLegacyIdsLoading || (isSectionUser && sectionsLoading);

  return {
    hasPrimarySection: !loading && primarySectionId !== null,
    primarySectionId,
    loading
  };
}
